import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  IconButton,
  alpha,
  useTheme,
  Tabs,
  Tab,
  Card,
  CardContent,
  Tooltip,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlightIcon from '@mui/icons-material/Flight';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Badge from '@mui/material/Badge';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import HubIcon from '@mui/icons-material/Hub';
import WarningIcon from '@mui/icons-material/Warning';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import MemoryIcon from '@mui/icons-material/Memory';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { default as api } from '../../../services/api';
import AuthenticatedImage from '../../common/AuthenticatedImage';
import { getAiServerStatus, getAiModelDetails } from '../../../services/aiService';
import Enhanced_AerialImageViewerModal from './Enhanced_AerialImageViewerModal';

const AerialInspectionDetailsModal = ({ open, onClose, inspection, onDataMutated }) => {
  const theme = useTheme();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [galleryViewMode, setGalleryViewMode] = useState('card');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [defectTypes, setDefectTypes] = useState([]);
  const [defectCounts, setDefectCounts] = useState({});
  const [defectTypesLoading, setDefectTypesLoading] = useState(false);
  const [selectedDefectTypeId, setSelectedDefectTypeId] = useState(null);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [aiAnalysisView, setAiAnalysisView] = useState(false);
  const [aiServerStatus, setAiServerStatus] = useState(null);
  const [aiModelDetails, setAiModelDetails] = useState(null);
  const [aiDataLoading, setAiDataLoading] = useState(false);
  const [aiDataError, setAiDataError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [aiJob, setAiJob] = useState({ id: null, status: 'IDLE', progress: null, error: null });
  
  // Image viewer modal state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  // Fetch photos when modal opens
  useEffect(() => {
    if (!open || !inspection?.id) return;
    
    const fetchPhotos = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get(`/aerial-inspections/${inspection.id}/photos`);
        const photosWithUrls = (resp.data || []).map(photo => ({
          ...photo,
          photo_path: photo.photo_path?.replace(/\\/g, '/'),
          url: photo.photo_path?.replace(/\\/g, '/')
        }));
        setPhotos(photosWithUrls);
      } catch (e) {
        console.error('Error fetching aerial inspection photos:', e);
        setError('Failed to load inspection photos');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPhotos();
    // Reset state when modal opens
    setTabValue(0);
    setUploadFiles([]);
    setUploadProgress(0);
    setError(null);
    setSuccess(false);
    setSelectedDefectTypeId(null);
  }, [open, inspection?.id]);

  // Fetch defect types and counts for aerial inspection
  useEffect(() => {
    if (!open || !inspection) return;
    
    const fetchDefectTypesAndCounts = async () => {
      console.log('[AerialInspectionDetailsModal] Fetching defect types and counts for aerial inspection:', inspection.id);
      setDefectTypesLoading(true);
      let localCountsByType = {};
      const photoDefectTypes = {};

      try {
        let organizationId = null;
        
        // Try to get organization_id from inspection
        if (inspection.organization_id) {
          organizationId = inspection.organization_id;
          console.log(`[AerialInspectionDetailsModal] Using inspection.organization_id: ${organizationId}`);
        }
        
        // Fetch all defect types
        let defectTypesData = [];
        
        try {
          const params = {};
          if (organizationId) {
            params.organization_id = organizationId;
          }
          
          console.log('[AerialInspectionDetailsModal] Fetching defect types with params:', params);
          const typesResponse = await api.get('/defect-types', { params });
          
          if (Array.isArray(typesResponse.data)) {
            // Filter only defect types that contain "Aerial" in the name
            defectTypesData = typesResponse.data.filter(type => 
              type.name && type.name.toLowerCase().includes('aerial')
            );
            console.log(`[AerialInspectionDetailsModal] Found ${defectTypesData.length} aerial defect types (filtered from ${typesResponse.data.length} total)`);
          }
        } catch (typesErr) {
          console.error('[AerialInspectionDetailsModal] Error fetching defect types:', typesErr);
        }
        
        setDefectTypes(prevDefectTypes => {
          if (JSON.stringify(prevDefectTypes) === JSON.stringify(defectTypesData)) return prevDefectTypes;
          return defectTypesData;
        });
        
        // Get photo IDs for this aerial inspection to fetch bounding boxes
        const photoIds = photos.map(photo => photo.id).filter(id => id);
        
        if (photoIds.length > 0) {
          try {
            // Fetch bounding boxes for each aerial photo in batches
            // Use aerial-inspections/bounding-boxes endpoint for aerial photos
            const batchSize = 5;
            for (let i = 0; i < photoIds.length; i += batchSize) {
              const batch = photoIds.slice(i, i + batchSize);
              const batchPromises = batch.map(photoId => 
                api.get(`/aerial-inspections/bounding-boxes`, {
                  params: {
                    photo_id: photoId
                  }
                })
              );
              
              const responses = await Promise.all(batchPromises);
              
              // Count bounding boxes by defect type
              responses.forEach((response, index) => {
                const photoId = batch[index];
                if (Array.isArray(response.data)) {
                  photoDefectTypes[photoId] = [];
                  
                  response.data.forEach(box => {
                    if (box.defect_type_id) {
                      const defectTypeId = parseInt(box.defect_type_id, 10);
                      localCountsByType[defectTypeId] = (localCountsByType[defectTypeId] || 0) + 1;
                      
                      if (!photoDefectTypes[photoId].includes(defectTypeId)) {
                        photoDefectTypes[photoId].push(defectTypeId);
                      }
                    }
                  });
                }
              });
            }
            
            // Update photos with defect type information
            setPhotos(prevPhotos => {
              let photosChanged = false;
              const updatedPhotos = prevPhotos.map(photo => {
                const newDefectTypes = photoDefectTypes[photo.id] || [];
                
                if (!photo.defect_types || JSON.stringify(photo.defect_types) !== JSON.stringify(newDefectTypes)) {
                  photosChanged = true;
                  return { 
                    ...photo, 
                    defect_types: newDefectTypes
                  };
                }
                return photo;
              });
              return photosChanged ? updatedPhotos : prevPhotos;
            });
            
            console.log('[AerialInspectionDetailsModal] Bounding box counts by defect type:', localCountsByType);
          } catch (boxError) {
            console.error('[AerialInspectionDetailsModal] Error fetching bounding boxes:', boxError);
          }
        }
        
        setDefectCounts(prevDefectCounts => {
          const newKeys = Object.keys(localCountsByType);
          const prevKeys = Object.keys(prevDefectCounts);
          if (newKeys.length === prevKeys.length && newKeys.every(key => prevDefectCounts[key] === localCountsByType[key])) {
            return prevDefectCounts;
          }
          return { ...localCountsByType };
        });
        
      } catch (err) {
        console.error('[AerialInspectionDetailsModal] Error in defect types and counts fetch:', err);
      } finally {
        setDefectTypesLoading(false);
      }
    };
    
    // Only fetch if we have inspection data and photos
    if (inspection?.id && photos.length > 0) {
      fetchDefectTypesAndCounts();
    }
  }, [open, inspection, photos]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle gallery view mode change
  const handleGalleryViewChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setGalleryViewMode(newViewMode);
    }
  };

  // Dropzone configuration
  const onDrop = useCallback(acceptedFiles => {
    const withPreview = acceptedFiles.map(f => 
      Object.assign(f, { preview: URL.createObjectURL(f) })
    );
    setUploadFiles(prev => [...prev, ...withPreview]);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    maxSize: 20 * 1024 * 1024
  });

  // Clean up previews when component unmounts
  useEffect(() => {
    return () => {
      uploadFiles.forEach(f => { 
        if (f.preview) URL.revokeObjectURL(f.preview); 
      });
    };
  }, [uploadFiles]);

  // Remove file from upload list
  const removeFile = (index) => {
    setUploadFiles(prev => {
      const clone = [...prev];
      const file = clone[index];
      if (file?.preview) URL.revokeObjectURL(file.preview);
      clone.splice(index, 1);
      return clone;
    });
  };

  // Handle upload submission
  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const form = new FormData();
      uploadFiles.forEach(file => form.append('photos', file));
      
      const response = await api.post(`/aerial-inspections/${inspection.id}/photos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(pct);
        }
      });
      
      console.log('Upload response:', response.data);
      
      // Refresh photos list
      const resp = await api.get(`/aerial-inspections/${inspection.id}/photos`);
      const photosWithUrls = (resp.data || []).map(photo => ({
        ...photo,
        photo_path: photo.photo_path?.replace(/\\/g, '/'),
        url: photo.photo_path?.replace(/\\/g, '/')
      }));
      setPhotos(photosWithUrls);
      
      // Clear upload files
      setUploadFiles([]);
      
      // Show success message
      setSuccess(`Successfully uploaded ${uploadFiles.length} photo${uploadFiles.length === 1 ? '' : 's'}`);
      
      // Switch to gallery tab
      setTabValue(0);
      
      // Call onDataMutated callback
      if (onDataMutated) {
        onDataMutated();
        console.log('Called onDataMutated after photo upload.');
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
      
    } catch (err) {
      console.error('Error uploading photos:', err);
      let msg = 'Failed to upload photos';
      if (err.response) {
        msg += `: ${err.response.status} - ${err.response.data?.error || ''}`;
      }
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (photo, event) => {
    event.stopPropagation();
    setPhotoToDelete(photo);
    setDeleteConfirmOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setPhotoToDelete(null);
  };

  // Handle photo deletion
  const handleDeletePhoto = async () => {
    if (!photoToDelete || !inspection) return;
    
    setDeleting(true);
    try {
      await api.delete(`/aerial-inspections/${inspection.id}/photos/${photoToDelete.id}`);
      
      // Remove photo from state
      setPhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
      
      // Show success message
      setSuccess(`Photo successfully deleted`);
      
      // Call onDataMutated callback
      if (onDataMutated) {
        onDataMutated();
        console.log('Called onDataMutated after photo deletion.');
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error deleting photo:', err);
      let msg = 'Failed to delete photo';
      if (err.response) {
        msg += `: ${err.response.status} - ${err.response.data?.error || ''}`;
      }
      setError(msg);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setDeleting(false);
      setPhotoToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle defect type chip click for filtering
  const handleDefectTypeClick = (defectTypeId) => {
    if (selectedDefectTypeId === defectTypeId) {
      setSelectedDefectTypeId(null);
    } else {
      setSelectedDefectTypeId(defectTypeId);
    }
  };

  // Calculate total number of annotations
  const getTotalAnnotationCount = () => {
    return Object.values(defectCounts).reduce((sum, count) => sum + count, 0);
  };

  // Get filtered photos based on selected defect type
  const getFilteredPhotos = () => {
    if (!selectedDefectTypeId) return photos;
    
    return photos.filter(photo => {
      return photo.defect_types && photo.defect_types.includes(selectedDefectTypeId);
    });
  };

  // Update chip scroll arrows
  const updateChipScrollArrows = () => {
    const container = document.getElementById('aerial-defect-chips-container');
    const leftButton = document.getElementById('aerial-defect-chips-left-scroll-button');
    const rightButton = document.getElementById('aerial-defect-chips-right-scroll-button');
    if (!container || !leftButton || !rightButton) return;
    
    leftButton.style.visibility = container.scrollLeft > 5 ? 'visible' : 'hidden';
    rightButton.style.visibility =
      container.scrollWidth > container.clientWidth &&
      container.scrollLeft + container.clientWidth < container.scrollWidth - 5
        ? 'visible'
        : 'hidden';
  };

  // useEffect to update arrows after chips render
  useEffect(() => {
    if (!defectTypesLoading && defectTypes.length > 0) {
      setTimeout(() => {
        updateChipScrollArrows();
        const container = document.getElementById('aerial-defect-chips-container');
        if (container && window.ResizeObserver) {
          const observer = new ResizeObserver(() => updateChipScrollArrows());
          observer.observe(container);
          return () => observer.disconnect();
        }
      }, 150);
    }
  }, [defectTypesLoading, defectTypes.length]);

  // Filter photos when defect type is selected
  useEffect(() => {
    if (!selectedDefectTypeId) {
      setFilteredPhotos(photos);
      return;
    }

    const filtered = photos.filter(photo => {
      return photo.defect_types && photo.defect_types.includes(selectedDefectTypeId);
    });
    setFilteredPhotos(filtered);
  }, [selectedDefectTypeId, photos]);

  // Function to fetch AI server data
  const fetchAiData = async () => {
    setAiDataLoading(true);
    setAiDataError(null);
    try {
      const [status, models] = await Promise.all([
        getAiServerStatus(),
        getAiModelDetails(),
      ]);
      setAiServerStatus(status);
      setAiModelDetails(models);
      setLastRefreshTime(new Date());
    } catch (err) {
      setAiDataError('Failed to connect to AI server.');
    } finally {
      setAiDataLoading(false);
    }
  };

  // Auto-select model when AI models are available (Pavement-aerial)
  useEffect(() => {
    if (aiModelDetails?.models && !selectedModel) {
      const matchingModels = aiModelDetails.models.filter(model => 
        model.model_type === 'Pavement-aerial'
      );
      if (matchingModels.length > 0) {
        setSelectedModel(matchingModels[0]); // Auto-select first matching Pavement-aerial model
        console.log('[AerialInspectionDetailsModal] Auto-selected Pavement-aerial model:', matchingModels[0]);
      }
    }
  }, [aiModelDetails, selectedModel]);

  // Toggle AI analysis view
  const toggleAiAnalysisView = () => {
    const newAiAnalysisView = !aiAnalysisView;
    setAiAnalysisView(newAiAnalysisView);
    
    if (newAiAnalysisView) {
      // Fetch AI data when entering AI view
      fetchAiData();
    }
  };

  // Reset AI view when modal closes
  useEffect(() => {
    if (!open) {
      setAiAnalysisView(false);
      setSelectedModel(null);
      setAiJob({ id: null, status: 'IDLE', progress: null, error: null });
    }
  }, [open]);

  // Function to handle AI analysis
  const handleRunAnalysis = async () => {
    if (!inspection || !inspection.id) {
      setError('Cannot start analysis: inspection ID is missing.');
      return;
    }

    setAiJob({ id: null, status: 'SUBMITTING', progress: null, error: null });

    try {
      // Prepare request body with selected model if available
      const requestBody = {};
      if (selectedModel && selectedModel.model_id) {
        requestBody.model_id = selectedModel.model_id;
      }

      // Call the aerial inspections AI analysis endpoint
      const response = await api.post(`/aerial-inspections/${inspection.id}/start-ai-analysis`, requestBody);

      // Validate response
      if (!response?.data?.job_id) {
        const msg = 'Failed to start AI analysis: server did not return a job ID.';
        setAiJob({ id: null, status: 'FAILED', progress: null, error: msg });
        setError(msg);
        return;
      }

      console.log('[Aerial AI] Job submitted successfully:', response.data.job_id);
      setAiJob({ id: response.data.job_id, status: 'PENDING', progress: null, error: null });
    } catch (err) {
      console.error('[Aerial AI] Error starting AI analysis:', err);
      const errorMessage = err.response?.data?.error || 'Failed to start AI analysis job.';
      setAiJob({ id: null, status: 'FAILED', progress: null, error: errorMessage });
      setError(errorMessage);
    }
  };

  // Handle photo selection to open viewer
  const handlePhotoSelect = (photo, photosList, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
    setImageViewerOpen(true);
  };

  // Handle navigation in image viewer
  const handleImageViewerNavigate = (newIndex) => {
    const filteredList = getFilteredPhotos();
    if (newIndex >= 0 && newIndex < filteredList.length) {
      setSelectedPhoto(filteredList[newIndex]);
      setSelectedPhotoIndex(newIndex);
    }
  };

  // Close image viewer
  const handleImageViewerClose = () => {
    setImageViewerOpen(false);
    setSelectedPhoto(null);
  };

  // Poll AI job status
  useEffect(() => {
    if (aiJob.id && (aiJob.status === 'PENDING' || aiJob.status === 'PROGRESS')) {
      const visionApiBaseUrl = 'https://vision-api.inxpect.cloud';
      const intervalId = setInterval(async () => {
        try {
          const response = await axios.get(`${visionApiBaseUrl}/jobs/${aiJob.id}/status`);
          const { status, progress, result } = response.data;

          if (status === 'SUCCESS') {
            clearInterval(intervalId);
            setAiJob(prev => ({ ...prev, status: 'SUCCESS', progress: null }));
            
            // Save results
            try {
              await api.post(`/aerial-inspections/${inspection.id}/save-ai-results`, { results: result.results });
              setSuccess('AI analysis results saved successfully.');
              if (onDataMutated) {
                onDataMutated();
              }
              
              // Keep SUCCESS status with result info
              setAiJob(prev => ({ 
                ...prev, 
                status: 'SUCCESS', 
                progress: null, 
                error: null,
                resultInfo: {
                  totalImages: result.results?.length || 0,
                  completedAt: new Date().toLocaleTimeString()
                }
              }));
              
              // Reset to IDLE after 10 seconds
              setTimeout(() => {
                setAiJob({ id: null, status: 'IDLE', progress: null, error: null, resultInfo: null });
              }, 10000);
            } catch (saveErr) {
              const errorMessage = saveErr.response?.data?.error || 'Failed to save AI results.';
              setError(errorMessage);
              setAiJob(prev => ({ ...prev, status: 'FAILED', error: errorMessage }));
            }
          } else if (status === 'FAILURE') {
            clearInterval(intervalId);
            const errorMessage = result || 'AI processing job failed.';
            setAiJob(prev => ({ ...prev, status: 'FAILED', error: errorMessage }));
            setError(errorMessage);
          } else {
            // Still in progress
            setAiJob(prev => ({ ...prev, status: 'PROGRESS', progress }));
          }
        } catch (err) {
          clearInterval(intervalId);
          const errorMessage = err.response?.data?.error || 'Failed to get job status.';
          setAiJob(prev => ({ ...prev, status: 'FAILED', error: errorMessage }));
          setError(errorMessage);
        }
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(intervalId);
    }
  }, [aiJob.id, aiJob.status, inspection, onDataMutated]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '80vh',
            maxHeight: '90vh',
            width: '100%',
            maxWidth: '1200px',
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa',
            backgroundImage: 'none',
            boxShadow: theme.shadows[8]
          }
        }}
      >
        {/* DIALOG TITLE / HEADER */}
        <DialogTitle sx={{ 
          pb: 0.5, 
          pt: 1.5,
          px: 2.5,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              {inspection?.title || 'Aerial Inspection Session'}
        </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {inspection?.inspection_date ? new Date(inspection.inspection_date).toLocaleString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : 'Date not specified'}
        </Typography>
          </Box>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {/* AI Analysis Button */}
            <Button
              variant={aiAnalysisView ? "contained" : "outlined"}
              color="primary"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={toggleAiAnalysisView}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 1.5,
                height: 32,
                bgcolor: aiAnalysisView ? (
                  theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.8) : theme.palette.primary.main
                ) : 'transparent',
                '&:hover': {
                  bgcolor: aiAnalysisView ? (
                    theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.dark
                  ) : alpha(theme.palette.primary.main, 0.08),
                }
              }}
            >
              {aiAnalysisView ? "Back to Gallery" : "AI Analysis"}
            </Button>
            
            {/* Close Button */}
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={onClose} 
              aria-label="close"
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
      </DialogTitle>
        
        {/* DEFECT CLASSIFICATION SUMMARY SECTION - Between Header and Tabs */}
        {!aiAnalysisView && (
          <Box sx={{ 
            px: 2.5, 
            py: 0.75,
            display: 'flex', 
            flexDirection: 'row',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
            minHeight: '40px',
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              minWidth: 'auto', 
              flexShrink: 0,
              pr: 1
            }}>
              <AssessmentIcon sx={{ mr: 0.75, color: theme.palette.info.main, fontSize: '1rem' }} />
              <Typography variant="subtitle2" fontWeight={600} fontSize="0.8rem" noWrap>
                Aerial Defect Classification Summary
              </Typography>
              {getTotalAnnotationCount() > 0 && (
                <Badge 
                  badgeContent={getTotalAnnotationCount()} 
                  color="secondary"
                  sx={{ ml: 1 }}
                  size="small"
                >
                  <BugReportIcon sx={{ color: theme.palette.secondary.main, fontSize: '0.9rem' }} />
                </Badge>
              )}
          </Box>
              
            {/* Display defect type chips */}
            {!defectTypesLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flexGrow: 1,
                position: 'relative',
                ml: 1,
                mr: 1,
              }}>
                {/* Left scroll arrow */}
                <IconButton 
                  id="aerial-defect-chips-left-scroll-button"
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    left: -5, 
                    zIndex: 2, 
                    p: 0.3,
                    visibility: 'hidden',
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    display: 'flex',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    boxShadow: theme.shadows[2]
                  }}
                  onClick={() => {
                    const container = document.getElementById('aerial-defect-chips-container');
                    if (container) {
                      container.scrollBy({ left: -100, behavior: 'smooth' });
                      setTimeout(() => {
                        if (container.scrollLeft <= 5) {
                          document.getElementById('aerial-defect-chips-left-scroll-button').style.visibility = 'hidden';
                        }
                        document.getElementById('aerial-defect-chips-right-scroll-button').style.visibility = 'visible';
                      }, 350);
                    }
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: '0.7rem' }} />
                  </IconButton>
                
                {/* Chips container */}
                <Box 
                  id="aerial-defect-chips-container"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.75,
                    px: 2,
                    overflow: 'auto',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none'
                    }
                  }}
                  onScroll={(e) => {
                    updateChipScrollArrows();
                  }}
                >
                  {defectTypes.map((type) => {
                    const typeId = typeof type.id === 'string' ? parseInt(type.id, 10) : type.id;
                    const count = defectCounts[typeId] || 0;
                    const isSelected = selectedDefectTypeId === typeId;
                    
                    return (
                      <Chip
                        key={type.id}
                        size="small"
                        label={`${type.name}: ${count}`}
                        color={isSelected ? "primary" : (count > 0 ? "secondary" : "default")}
                        variant={isSelected ? "filled" : "outlined"}
                        onClick={() => handleDefectTypeClick(typeId)}
                        sx={{ 
                          height: 22,
                          flexShrink: 0,
                          cursor: 'pointer',
                          '& .MuiChip-label': { 
                            px: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }
                        }}
                      />
                    );
                  })}
                  
                  {defectTypes.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No aerial defect types found
                    </Typography>
                  )}
                </Box>
                
                {/* Right scroll arrow */}
                <IconButton 
                  id="aerial-defect-chips-right-scroll-button"
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    right: -5, 
                    zIndex: 2, 
                    p: 0.3,
                    visibility: 'visible',
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    display: 'flex',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    boxShadow: theme.shadows[2]
                  }}
                  onClick={() => {
                    const container = document.getElementById('aerial-defect-chips-container');
                    if (container) {
                      container.scrollBy({ left: 100, behavior: 'smooth' });
                      setTimeout(() => {
                        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 5) {
                          document.getElementById('aerial-defect-chips-right-scroll-button').style.visibility = 'hidden';
                        }
                        if (container.scrollLeft > 5) {
                          document.getElementById('aerial-defect-chips-left-scroll-button').style.visibility = 'visible';
                        }
                      }, 350);
                    }
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: '0.7rem' }} />
                </IconButton>
            </Box>
            ) : (
              <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={14} thickness={4} />
                <Typography variant="caption" sx={{ ml: 1 }}>Loading defect types...</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* TABS - Only show when not in AI Analysis view */}
        {!aiAnalysisView && (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
            minHeight: 48,
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
            '& .MuiTabs-indicator': {
              height: 3,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              backgroundColor: theme.palette.primary.main
            },
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: '0.9rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent'
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.03)
              }
            }
          }}
        >
          <Tab 
            label="Gallery" 
            icon={<ImageIcon sx={{ fontSize: '1.2rem' }} />} 
            iconPosition="start" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            label="Upload" 
            icon={<UploadIcon sx={{ fontSize: '1.2rem' }} />} 
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          </Tabs>
        )}
        
        {/* DIALOG CONTENT */}
        <DialogContent 
          sx={{ 
            p: 0,
            display: 'flex', 
            flexDirection: 'column',
            flex: 1,
            overflowX: 'hidden',
            backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa'
          }}
        >
          {/* AI ANALYSIS VIEW */}
          {aiAnalysisView ? (
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* AI Analysis Header */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  mb: 3, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 2, fontSize: '1.125rem' }}>
                  AI Defect Analysis for Aerial Inspection
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" fontSize="0.7rem" textTransform="uppercase" fontWeight={600} gutterBottom>
                      Inspection Details
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontSize: '0.95rem', fontWeight: 600 }}>
                      {inspection?.title || 'Untitled Aerial Inspection'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                      Date: {inspection?.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </Typography>
              </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" fontSize="0.7rem" textTransform="uppercase" fontWeight={600} gutterBottom>
                      AI Summary
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>Total Images:</span>
                        <span style={{ minWidth: '30px', textAlign: 'right' }}>{photos.length}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>Total AI Detections:</span>
                        <span style={{ minWidth: '30px', textAlign: 'right' }}>{getTotalAnnotationCount()}</span>
                      </Typography>
                    </Box>
          </Grid>
                </Grid>
        </Paper>

              {/* AI Server Status */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                      AI Server Status
                    </Typography>
                    {lastRefreshTime && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Last updated: {lastRefreshTime.toLocaleDateString('en-GB')} at {lastRefreshTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      onClick={fetchAiData}
                      disabled={aiDataLoading}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                      }}
                    >
                      <RefreshIcon 
                        sx={{ 
                          fontSize: 18,
                          color: theme.palette.primary.main,
                          animation: aiDataLoading ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }} 
                      />
                  </IconButton>
                    {aiDataLoading && <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />}
                  </Box>
                </Box>

                {aiDataLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : aiDataError ? (
                  <Alert severity="error">{aiDataError}</Alert>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
                      gap: 2
                    }}
                  >
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', bgcolor: aiServerStatus?.api_server_status === 'Online' ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.error.main, 0.05) }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: aiServerStatus?.api_server_status === 'Online' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        {aiServerStatus?.api_server_status === 'Online' ? (
                          <CloudDoneIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                        ) : (
                          <CloudOffIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>API Server</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: aiServerStatus?.api_server_status === 'Online' ? theme.palette.success.main : theme.palette.error.main }}>
                        {aiServerStatus?.api_server_status || 'Offline'}
                      </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', bgcolor: aiServerStatus?.ai_processor_status === 'Online' ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.error.main, 0.05) }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: aiServerStatus?.ai_processor_status === 'Online' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <HubIcon sx={{ color: aiServerStatus?.ai_processor_status === 'Online' ? theme.palette.success.main : theme.palette.error.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>AI Processor</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: aiServerStatus?.ai_processor_status === 'Online' ? theme.palette.success.main : theme.palette.error.main }}>
                        {aiServerStatus?.ai_processor_status || 'Offline'}
                      </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <WarningIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>Health Summary</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{aiServerStatus?.health_summary || 'N/A'}</Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: alpha(theme.palette.secondary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <PlaylistPlayIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>Active Jobs</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', color: theme.palette.secondary.main }}>
                        {aiServerStatus?.active_inference_jobs ?? '0'}
                      </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <DeveloperBoardIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>CPU / RAM</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>CPU: {aiServerStatus?.cpu_load_percent || '0'}%</Box>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>RAM: {aiServerStatus?.ram_load_percent || '0'}%</Box>
                      </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 1.5, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: alpha(theme.palette.warning.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <MemoryIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>GPU Usage</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>Load: {aiServerStatus?.gpu_load_percent || '0'}%</Box>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>Memory: {aiServerStatus?.gpu_memory_percent ? parseFloat(aiServerStatus.gpu_memory_percent).toFixed(1) : '0.0'}%</Box>
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Paper>

              {/* AI Model Coverage */}
              <Paper
                elevation={0}
                sx={{ p: 3, mb: 3, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2, backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff' }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  AI Model Coverage
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {aiDataLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : aiDataError ? (
                  <Alert severity="error">{aiDataError}</Alert>
                ) : (() => {
                  const matchingModels = aiModelDetails?.models?.filter(m => m.model_type === 'Pavement-aerial') || [];
                  const currentModel = selectedModel || matchingModels[0];
                  return (
                    <>
                      {matchingModels.length > 1 ? (
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                          <InputLabel id="ai-model-select-label">Select AI Model</InputLabel>
                          <Select
                            labelId="ai-model-select-label"
                            id="ai-model-select"
                            value={currentModel?.model_id || ''}
                            label="Select AI Model"
                            onChange={(e) => {
                              const selected = matchingModels.find(m => m.model_id === e.target.value);
                              setSelectedModel(selected || null);
                            }}
                            sx={{ bgcolor: 'background.paper', '& .MuiSelect-select': { fontSize: '0.875rem' } }}
                          >
                            {matchingModels.map((m) => (
                              <MenuItem key={m.model_id} value={m.model_id}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.description} v{m.model_version}</Typography>
                                  <Typography variant="caption" color="text.secondary">{Object.keys(m.classes).length} defect types • ID: {m.model_id}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : currentModel ? (
                        <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', mt: 1 }}>
                          Using: {currentModel.description} v{currentModel.model_version}
                        </Typography>
                      ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          No matching AI model found for model_type "Pavement-aerial".
                        </Alert>
                      )}

                      {currentModel?.classes && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          {Object.values(currentModel.classes).map((className, idx) => (
                            <Grid item xs={6} sm={4} md={3} key={idx}>
                              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: theme.palette.divider, display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: idx % 4 === 0 ? theme.palette.success.main : idx % 4 === 1 ? theme.palette.info.main : idx % 4 === 2 ? theme.palette.warning.main : theme.palette.error.main, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{className}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
                    </>
                  );
                })()}
              </Paper>
              
              {/* AI Processing Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 0, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  AI Processing
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* AI Job Status Display */}
                  {(aiJob.status === 'SUBMITTING' || aiJob.status === 'PENDING' || aiJob.status === 'PROGRESS') && (
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderColor: 'primary.main' }}>
                      <CircularProgress size={24} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {aiJob.status === 'SUBMITTING' && 'Submitting Job...'}
                          {aiJob.status === 'PENDING' && 'Job Queued, Awaiting Processor...'}
                          {aiJob.status === 'PROGRESS' && `Analyzing Image: ${aiJob.progress?.current} of ${aiJob.progress?.total}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {aiJob.status === 'PROGRESS' && `File: ${aiJob.progress?.filename}`}
                          {aiJob.status !== 'PROGRESS' && 'This may take several minutes.'}
                        </Typography>
            </Box>
                    </Paper>
                  )}

                  {aiJob.status === 'SUCCESS' && (
                    <Alert 
                      severity="success" 
                      sx={{ 
                        '& .MuiAlert-message': { width: '100%' },
                        borderColor: 'success.main'
                      }}
                      icon={<CheckCircleIcon />}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                            ✨ AI Analysis Completed Successfully!
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {aiJob.resultInfo?.totalImages} images processed • Completed at {aiJob.resultInfo?.completedAt}
                          </Typography>
          </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => setAiJob({ id: null, status: 'IDLE', progress: null, error: null, resultInfo: null })}
                          sx={{ textTransform: 'none', minWidth: 'auto' }}
                        >
                          Dismiss
                        </Button>
                      </Box>
                    </Alert>
                  )}

                  {aiJob.status === 'FAILED' && (
                    <Alert severity="error">
                      <strong>Analysis Failed:</strong> {aiJob.error}
                    </Alert>
                  )}

                  {!selectedModel && !aiDataLoading && (
                    <Alert severity="info">
                      Loading AI models... Please wait or refresh if this takes too long.
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    color={aiJob.status === 'SUCCESS' ? "success" : "primary"}
                    fullWidth
                    startIcon={aiJob.status === 'SUCCESS' ? <CheckCircleIcon /> : <AutoAwesomeIcon />}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      bgcolor: aiJob.status === 'SUCCESS' ? theme.palette.success.main : theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: aiJob.status === 'SUCCESS' ? theme.palette.success.dark : theme.palette.primary.dark,
                      },
                      borderRadius: 1.5
                    }}
                    onClick={handleRunAnalysis}
                    disabled={aiJob.status === 'SUBMITTING' || aiJob.status === 'PENDING' || aiJob.status === 'PROGRESS' || !selectedModel}
                  >
                    {aiJob.status === 'SUBMITTING' && 'Submitting Job...'}
                    {aiJob.status === 'PENDING' && 'Job Queued...'}
                    {aiJob.status === 'PROGRESS' && `Analyzing... (${aiJob.progress?.current}/${aiJob.progress?.total})`}
                    {aiJob.status === 'SUCCESS' && '✅ Analysis Complete - Run Again'}
                    {(aiJob.status === 'IDLE' || aiJob.status === 'FAILED') && 'Run AI Analysis on All Images'}
                  </Button>
                </Box>
              </Paper>
            </Box>
          ) : (
            <>
              {/* GALLERY TAB */}
              <Box 
            sx={{ 
              display: tabValue === 0 ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              height: '100%',
              width: '100%'
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mx: 2.5, mt: 2 }}>
                {success}
              </Alert>
            )}
            
            {/* Controls Bar */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : '#fff'
            }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {selectedDefectTypeId ? (
                  <>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      Showing {getFilteredPhotos().length} of {photos.length} photos with selected defect
                      <Button 
                        size="small" 
                        variant="outlined"
                        color="primary"
                        sx={{ ml: 1.5, textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                        onClick={() => setSelectedDefectTypeId(null)}
                      >
                        Clear filter
                      </Button>
                    </Box>
                  </>
                ) : (
                  `${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`
                )}
              </Typography>
              
              <ToggleButtonGroup
                value={galleryViewMode}
                exclusive
                onChange={handleGalleryViewChange}
                aria-label="gallery view mode"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 1.5,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    px: 1.5,
                    '&:not(:first-of-type)': {
                      borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.25)
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="card" aria-label="card view">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Gallery Content */}
            <Box sx={{ 
              p: 2.5, 
              flex: 1, 
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
        {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 10 }}>
                  <CircularProgress size={32} thickness={4} />
          </Box>
              ) : getFilteredPhotos().length > 0 ? (
                <>
                  {/* Card View */}
                  {galleryViewMode === 'card' && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 2,
                      justifyContent: 'flex-start',
                      width: '100%'
                    }}>
                      {getFilteredPhotos().map((photo, index) => (
                        <Card
                          key={photo.id || index}
                          sx={{
                            width: 300,
                            height: 380,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            boxShadow: theme.shadows[2],
                            transition: 'box-shadow 0.2s, transform 0.2s',
                            '&:hover': {
                              boxShadow: theme.shadows[6],
                              transform: 'translateY(-4px)'
                            },
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            position: 'relative'
                          }}
                        >
                          <AuthenticatedImage
                            src={photo.url}
                            alt={`Photo ${index + 1}`}
                            sx={{
                              height: 200,
                              objectFit: 'cover',
                              flexShrink: 0,
                              cursor: 'pointer'
                            }}
                            onClick={() => handlePhotoSelect(photo, getFilteredPhotos(), index)}
                          />
                          
                          {/* Delete Button Overlay */}
                          <Tooltip title="Delete photo" arrow placement="left">
                            <IconButton
                              size="small"
                              onClick={(e) => openDeleteConfirm(photo, e)}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                bgcolor: alpha(theme.palette.error.main, 0.9),
                                color: '#fff',
                                '&:hover': {
                                  bgcolor: theme.palette.error.main,
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s',
                                boxShadow: theme.shadows[2]
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          <CardContent 
                            sx={{
                              p: 1.5,
                              height: 180,
                              overflow: 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                              flexShrink: 0,
                              '&:last-child': { pb: 1.5 },
                              borderTop: '1px solid',
                              borderColor: alpha(theme.palette.divider, 0.5),
                              backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : '#fff'
                            }}
                          >
                            {photo.is_drone_photo && (
                              <Box sx={{ mb: 0.5 }}>
                                <Chip 
                                  icon={<FlightIcon />} 
                                  label="Drone Photo"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </Box>
                            )}
                            
                            {photo.caption && (
                              <Typography 
                                variant="body2" 
                                fontWeight="medium"
                                sx={{ 
                                  mb: 0.5, 
                                  flexShrink: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {photo.caption}
                              </Typography>
                            )}
                            
                            {(photo.latitude || photo.longitude) && (
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mt: 0.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderRadius: 1,
                                p: 0.5,
                                pl: 1
                              }}>
                                <LocationOnIcon color="primary" sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography variant="caption" color="text.primary" fontWeight="medium">
                                  {photo.latitude ? photo.latitude.toFixed(6) : '?'}, 
                                  {photo.longitude ? photo.longitude.toFixed(6) : '?'}
                                  {photo.altitude ? ` @ ${photo.altitude.toFixed(1)}m` : ''}
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ mt: 'auto', pt: 0.5 }}>
                              {photo.taken_at && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                                  <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.info.main }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(photo.taken_at).toLocaleDateString(undefined, {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </Typography>
                                </Box>
                              )}
                              <Typography variant="caption" color="text.disabled">
                                ID: {photo.id || 'N/A'}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}

                  {/* List View */}
                  {galleryViewMode === 'list' && (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1
                    }}>
                      {/* Table Header */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '72px 1fr 180px 180px 60px',
                        alignItems: 'center',
                        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.light, 0.1),
                        borderBottom: '2px solid',
                        borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : theme.palette.primary.light,
                        px: 2,
                        py: 1.25,
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                      }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                          Thumbnail
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                          File Name
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                          Location
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                          Date
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase', textAlign: 'center' }}>
                          Actions
                        </Typography>
                      </Box>
                      
                      {/* Table Body */}
                      <Box sx={{ 
                        flex: 1,
                        overflow: 'auto'
                      }}>
                        {getFilteredPhotos().map((photo, index) => {
                          let locationText = 'N/A';
                          if (photo.latitude && photo.longitude) {
                            locationText = `${photo.latitude.toFixed(4)}, ${photo.longitude.toFixed(4)}`;
                            if (photo.altitude) {
                              locationText += ` @ ${photo.altitude.toFixed(0)}m`;
                            }
                          }
                          
                          const dateText = photo.taken_at 
                            ? new Date(photo.taken_at).toLocaleDateString(undefined, { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric'
                              })
                            : 'Unknown';
                          
                          return (
                            <Box 
                              key={photo.id || index}
                              sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: '72px 1fr 180px 180px 60px',
                                alignItems: 'center',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                px: 2,
                                py: 1,
                                transition: 'all 0.2s',
                                bgcolor: index % 2 === 0 
                                  ? (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff')
                                  : (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.grey[50], 0.5)),
                                '&:hover': {
                                  bgcolor: theme.palette.mode === 'dark' 
                                    ? alpha(theme.palette.primary.main, 0.1) 
                                    : alpha(theme.palette.primary.light, 0.1),
                                }
                              }}
                            >
                              {/* Thumbnail */}
                              <Box
                                sx={{
                                  width: 60,
                                  height: 45,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.paper',
                                  boxShadow: 1,
                                  cursor: 'pointer'
                                }}
                                onClick={() => handlePhotoSelect(photo, getFilteredPhotos(), index)}
                              >
                                <AuthenticatedImage
                                  src={photo.url}
                                  alt={`Thumbnail ${index + 1}`}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              </Box>
                              
                              {/* File Name */}
                              <Box sx={{ 
                                pl: 2, 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {photo.caption || `Photo ${index + 1}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  ID: {photo.id || 'N/A'}
                                </Typography>
                              </Box>
                              
                              {/* Location */}
                              <Box>
                                {photo.latitude && photo.longitude ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocationOnIcon 
                                      color="primary" 
                                      sx={{ fontSize: 16, mr: 0.5 }} 
                                    />
                                    <Typography variant="caption">
                                      {locationText}
                                    </Typography>
          </Box>
        ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    No location data
                                  </Typography>
                                )}
                              </Box>
                              
                              {/* Date */}
                              <Box>
                                {photo.taken_at ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CalendarTodayIcon 
                                      sx={{ fontSize: 14, mr: 0.5, color: theme.palette.info.main }} 
                                    />
                                    <Typography variant="caption">
                                      {dateText}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    Unknown date
                                  </Typography>
                                )}
                              </Box>
                              
                              {/* Actions */}
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Tooltip title="Delete photo">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={(e) => openDeleteConfirm(photo, e)}
                                    sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.1),
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center',
                  alignItems: 'center', 
                  height: '100%', 
                  py: 8,
                  color: 'text.secondary'
                }}>
                  {selectedDefectTypeId && photos.length > 0 ? (
                    <>
                      <BugReportIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                      <Typography variant="h6">No Photos With Selected Defect</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        None of the photos contain the selected defect type.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setSelectedDefectTypeId(null)}
                        sx={{ mt: 3 }}
                      >
                        Show All Photos
                      </Button>
                    </>
                  ) : (
                    <>
                      <ImageIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                      <Typography variant="h6">No Photos Available</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        This aerial inspection doesn't have any photos yet.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<UploadIcon />}
                        onClick={() => setTabValue(1)}
                        sx={{ mt: 3 }}
                      >
                        Upload Photos
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          
          {/* UPLOAD TAB */}
          <Box 
            sx={{ 
              display: tabValue === 1 ? 'flex' : 'none',
              flexDirection: 'column',
              p: 2.5,
              flex: 1,
              height: '100%',
              width: '100%'
            }}
          >
            <Paper
              {...getRootProps()}
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                mb: 3
              }}
            >
          <input {...getInputProps()} />
          <Box sx={{ textAlign: 'center' }}>
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Drop images here' : 'Drag & Drop'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isDragActive
                    ? 'Release to add images'
                    : 'Drop image files here, or click to select files'}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                  Accepts JPG, PNG and GIF images up to 20MB
                </Typography>
          </Box>
        </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
        )}

        {uploading && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading... {uploadProgress}%
                </Typography>
                <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: 8,
                      width: `${uploadProgress}%`,
                      bgcolor: 'primary.main',
                      transition: 'width 0.2s ease'
                    }}
                  />
            </Box>
          </Box>
        )}

            {uploadFiles.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {uploadFiles.map((file, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 1,
                        height: 200
                      }}
                    >
                      <Box
                        component="img"
                        src={file.preview}
                        alt={`Preview ${index}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255,0,0,0.7)',
                          }
                        }}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
          </Box>
            </>
        )}
      </DialogContent>
        
        {/* DIALOG ACTIONS */}
        <DialogActions 
          sx={{ 
            p: 2, 
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff'
          }}
        >
          <Button 
            onClick={onClose} 
            color="inherit"
            sx={{
              fontWeight: 500,
              textTransform: 'none',
              px: 2
            }}
          >
            Close
          </Button>
          
          {tabValue === 1 && (
            <Button
              variant="contained"
              color="primary"
              disabled={uploadFiles.length === 0 || uploading}
              onClick={handleUpload}
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                px: 2
              }}
            >
          {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} ${uploadFiles.length === 1 ? 'Photo' : 'Photos'}`}
        </Button>
          )}
      </DialogActions>
    </Dialog>
      
      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Photo
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete this photo? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closeDeleteConfirm} 
            color="inherit"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePhoto} 
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
      
      {/* AERIAL IMAGE VIEWER MODAL */}
      {selectedPhoto && (
        <Enhanced_AerialImageViewerModal
          open={imageViewerOpen}
          onClose={handleImageViewerClose}
          photo={selectedPhoto}
          photos={getFilteredPhotos()}
          currentIndex={selectedPhotoIndex}
          onNavigate={handleImageViewerNavigate}
          onDataMutated={onDataMutated}
          viewOnlyMode={false}
        />
      )}
    </>
  );
};

export default AerialInspectionDetailsModal;


